import { actions, assign, raise, TransitionsConfig } from "xstate"
import { AirParams, CheckoutProcessId, SingleFareOptions, VsOption } from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import * as A from "fp-ts/Array"
import { assertEvent } from "../../../../_common/xstate-helpers"
import { getFromNormalQuery } from "../../../../_common/routing"
import { FareCard, FeasibleAirFares } from "../../read-models/fare-card"
import { fareCardKey, vsEligibility } from "../../read-models/fare-card/to-fare-cards"
import { fareCodeLabel } from "../../../../_common/format/fare-code-label"
import { featureFlag } from "../../../../_common/hooks/feature-flags"
import { noFlightKey } from "../../../flight/common/utils"
import { ECommerceEvent, ECommerceStateNodeConfig } from "./e-commerce-machine"
import { notifyInvalidParameter } from "./e-commerce-machine-helpers"
import { trackingEventActions } from "./actions/tracking-event-actions"
import { submitStatusActions } from "./actions/submit-status-actions"
import { fareCardActions } from "./actions/fare-card-actions"
import { webQuoteActions } from "./actions/web-quote-actions"
import { checkoutActions } from "./actions/checkout-actions"
import { paymentActions } from "./actions/payment-actions"
import { vsCodeActions } from "./actions/vs-code-actions"
import { ECommerceContext } from "./e-commerce-machine-context"
import {
    raiseFlightSelected,
    raiseGuestsChanged,
    raiseStandardSuiteCategorySelected,
    raiseSuiteNumberSelected,
} from "./actions/flow-actions"

const commonTransitionsV4: TransitionsConfig<ECommerceContext, ECommerceEvent> = {
    CHANGE_GUESTS: { actions: ["setGuests"] },
    CHANGE_VS_MEMBER: { actions: ["setVsCode"] },
    CHANGE_GATEWAY: { actions: ["updateFareCardGateway"] },
    CHANGE_AIR_CLASS: { actions: ["updateFareCardAirClass"] },

    GUESTS_CHANGED: { target: "nothingSelectedV4" },
}

export const flowConfig = (nothingSelected: string): ECommerceStateNodeConfig => ({
    initial: "uninitialized",
    states: {
        uninitialized: {
            on: { INITIALIZE: { target: "restoringCheckout" } },
        },
        restoringCheckout: {
            entry: ["restoreCheckout"],
            on: {
                NO_CHECKOUT_TO_RESTORE: { target: nothingSelected },
                LOAD_EXISTING_CHECKOUT: { actions: ["setSessionId"] },
                CHECKOUT_STARTED: [
                    {
                        cond: "isCourtesyHoldPage",
                        target: "#ecommerce.flow.courtesyHold.validated",
                        actions: ["setCheckoutResponse", "restoreContextFromCheckout"],
                    },
                    {
                        target: "#ecommerce.flow.checkout.validated",
                        actions: ["setCheckoutResponse", "restoreContextFromCheckout"],
                    },
                ],
                CHECKOUT_COMPLETED: {
                    target: "readyToCheckout.completed",
                    actions: ["setCheckoutResponse", "restoreContextFromCheckout"],
                },
                CHECKOUT_FAILED: {
                    actions: ["setCheckoutResponse", "raiseCheckoutRestored", "raiseFailedNotification"],
                },
            },
        },

        // New states for V4
        nothingSelectedV4: {
            entry: [
                "clearSelectedSuiteCategory",
                "clearSelectedFare",
                "clearSelectedSuiteNumber",
                "clearSelectedFlight",
            ],
            initial: "suiteCategoryUnselectable",
            states: {
                suiteCategoryUnselectable: {
                    on: {
                        SUITE_PRICES_LOADED: { target: "suiteCategorySelectable" },
                    },
                },
                suiteCategorySelectable: {
                    on: {
                        GUESTS_CHANGED: { target: "suiteCategoryUnselectable" },
                        SUITE_CATEGORY_SELECTED: { target: "#ecommerce.flow.suiteCategorySelectedV4" },
                    },
                },
            },
            on: {
                ...commonTransitionsV4,

                SELECT_SUITE_CATEGORY: { actions: ["setSelectedSuiteCategory"] },
            },
        },
        suiteCategorySelectedV4: {
            entry: [
                "clearSelectedFare",
                "clearSelectedSuiteNumber",
                "clearSelectedFlight",
                "setAvailableFareCardsForSelectedSuiteCategory",
            ],
            on: {
                ...commonTransitionsV4,

                SELECT_SUITE_CATEGORY: { actions: ["setSelectedSuiteCategory"] },
                SUITE_CATEGORY_SELECTED: { actions: ["setAvailableFareCardsForSelectedSuiteCategory"] },

                SELECT_FARE: { actions: ["setSelectedFare"] },
                FARE_SELECTED: { target: "fareSelectedV4" },

                UNSELECT_SUITE_CATEGORY: { actions: "clearSelectedSuiteCategory" },
                SUITE_CATEGORY_UNSELECTED: { target: "#ecommerce.flow.nothingSelectedV4.suiteCategorySelectable" },

                //TODO - v4: review events for quote if needed (SELECT_QUOTE_SUITE_CATEGORY)
            },
        },
        fareSelectedV4: {
            entry: ["clearSelectedSuiteNumber", "clearSelectedFlight"],
            initial: "suiteNumberUnselectable",
            states: {
                suiteNumberUnselectable: {
                    on: {
                        AVAILABLE_SUITES_LOADED: { target: "suiteNumberSelectable" },

                        //TODO - v4: review events for quote if needed (SELECT_QUOTE_SUITE_CATEGORY)
                    },
                },
                suiteNumberSelectable: {
                    on: {
                        FARE_SELECTED: { target: "suiteNumberUnselectable" },
                        SUITE_NUMBER_SELECTED: { target: "#ecommerce.flow.suiteNumberSelectedV4" },

                        //TODO - v4: review events for quote if needed (SELECT_QUOTE_SUITE_CATEGORY)
                    },
                },
            },
            on: {
                ...commonTransitionsV4,

                UNSELECT_FARE: { actions: "clearSelectedFare" },
                FARE_UNSELECTED: { target: "#ecommerce.flow.suiteCategorySelectedV4" },

                SELECT_SUITE_CATEGORY: { actions: ["setSelectedSuiteCategory"] },
                SUITE_CATEGORY_SELECTED: { target: "suiteCategorySelectedV4" },

                SELECT_FARE: { actions: ["setSelectedFare"] },

                //TODO - v4: review events for quote if needed (SELECT_QUOTE_SUITE_CATEGORY)

                SELECT_SUITE_NUMBER: { actions: ["setSelectedSuiteNumber"] },

                VALIDATE_WEB_QUOTE: { target: "webQuote", actions: ["setWebQuoteGuestInfo"] },
            },
        },
        suiteNumberSelectedV4: {
            entry: ["submitStatusReset", "clearSelectedFlight", "setNoFlight"],
            on: {
                ...commonTransitionsV4,

                SELECT_FARE: { actions: ["setSelectedFare"] },
                FARE_SELECTED: { target: "fareSelectedV4" },

                SELECT_SUITE_CATEGORY: { actions: ["setSelectedSuiteCategory"] },
                SUITE_CATEGORY_SELECTED: { target: "suiteCategorySelectedV4" },

                //TODO - v4: review events for quote if needed

                SELECT_SUITE_NUMBER: { actions: ["setSelectedSuiteNumber"] },
                UNSELECT_SUITE_NUMBER: { actions: "clearSelectedSuiteNumber" },
                SUITE_NUMBER_UNSELECTED: { target: "#ecommerce.flow.fareSelectedV4.suiteNumberSelectable" },

                SELECT_FLIGHT: { actions: ["setSelectedFlight"] },
                FLIGHT_SELECTED: { target: "flightSelected" },
            },
        },

        flightSelected: {
            entry: [],
            initial: "flightUnselectable",
            states: {
                flightUnselectable: {
                    on: {
                        AVAILABLE_FLIGHTS_LOADED: { target: "flightSelectable" },
                    },
                },
                flightSelectable: {
                    on: {
                        FLIGHT_SEARCH_EXPIRED: { target: "flightUnselectable" },
                        FLIGHT_SELECTED: { target: "#ecommerce.flow.flightSelected" },
                    },
                },
            },
            on: {
                ...commonTransitionsV4,

                SELECT_FARE: { actions: ["setSelectedFare"] },
                FARE_SELECTED: { target: "fareSelectedV4" },

                SELECT_SUITE_CATEGORY: { actions: ["setSelectedSuiteCategory"] },
                SUITE_CATEGORY_SELECTED: { target: "suiteCategorySelectedV4" },

                //TODO - v4: review events for quote if needed

                SELECT_SUITE_NUMBER: { actions: ["setSelectedSuiteNumber"] },
                SUITE_NUMBER_SELECTED: { target: "suiteNumberSelectedV4" },
                UNSELECT_SUITE_NUMBER: { actions: "clearSelectedSuiteNumber" },
                SUITE_NUMBER_UNSELECTED: { target: "#ecommerce.flow.fareSelectedV4.suiteNumberSelectable" },

                VALIDATE_VS_CODE: { actions: ["clearCheckoutResponse"] },
                RESET_VS_CODE: { actions: ["setVsCode", "clearCheckoutResponse"] },
                VS_CODE_VALID: { actions: ["setVsCode"] },
                VS_CODE_NOT_VALID: { actions: ["setVsCode"] },

                VALIDATE_CHECKOUT: { target: "checkout", actions: ["setGuestsInfo"] },
                VALIDATE_COURTESY_HOLD: { target: "courtesyHold", actions: ["setGuestsInfo"] },
                VALIDATE_WEB_QUOTE: { target: "webQuote", actions: ["setWebQuoteGuestInfo"] },
                LOAD_EXISTING_CHECKOUT: { target: "checkout", actions: ["setSessionId"] },
            },
        },

        webQuote: {
            initial: "validatingWebQuote",
            states: {
                validatingWebQuote: {
                    entry: ["submitStatusStart"],
                    on: {
                        WEB_QUOTE_VALIDATED: { target: "webQuoteValidated", actions: ["setWebQuoteResponse"] },
                        WEB_QUOTE_NOT_VALIDATED: { target: "#ecommerce.flow.suiteNumberSelectedV4" }, //TODO review destination state
                    },
                },
                webQuoteValidated: {
                    entry: ["trackingEventWebQuoteSubmitted"],
                    type: "final",
                },
            },
        },
        courtesyHold: {
            initial: "validating",
            states: {
                validating: {
                    entry: ["submitStatusStart"],
                    on: {
                        CHECKOUT_VALIDATED: { actions: ["setCheckoutResponse"] },
                        CHECKOUT_NOT_VALIDATED: {
                            target: "#ecommerce.flow.suiteNumberSelectedV4",
                            actions: ["setCheckoutResponse", "raiseValidationNotification"],
                        },

                        CHECKOUT_STARTED: { target: "validated", actions: ["setCheckoutResponse"] },
                        CHECKOUT_COMPLETED: { target: "validated", actions: ["setCheckoutResponse"] },
                        CHECKOUT_FAILED: {
                            target: "#ecommerce.flow.suiteNumberSelectedV4",
                            actions: ["setCheckoutResponse", "raiseFailedNotification"],
                        },
                    },
                },
                validated: {
                    entry: ["trackingEventCourtesyHold"],
                    type: "final",
                },
            },
        },
        checkout: {
            initial: "validating",
            states: {
                validating: {
                    entry: ["submitStatusStart"],
                    on: {
                        CHECKOUT_VALIDATED: { actions: ["setCheckoutResponse"] },
                        CHECKOUT_NOT_VALIDATED: {
                            target: "#ecommerce.flow.suiteNumberSelectedV4",
                            actions: ["setCheckoutResponse", "raiseValidationNotification"],
                        },

                        CHECKOUT_STARTED: {
                            target: "validated",
                            actions: ["setCheckoutResponse", "trackingEventGuestsFormSubmitted"],
                        },
                        CHECKOUT_COMPLETED: {
                            target: "#ecommerce.flow.readyToCheckout.completed",
                            actions: ["setCheckoutResponse"],
                        },
                        CHECKOUT_FAILED: {
                            target: "#ecommerce.flow.suiteNumberSelectedV4",
                            actions: ["setCheckoutResponse", "raiseFailedNotification"],
                        },
                    },
                },
                validated: {
                    entry: ["submitStatusComplete"],
                    on: {
                        START_PAYMENT: { target: "#ecommerce.flow.readyToCheckout", actions: ["setPaymentTrackingId"] },
                    },
                },
            },
        },
        readyToCheckout: {
            initial: "starting",
            states: {
                starting: {
                    on: {
                        PAYMENT_STARTED: { target: "paying", actions: ["setPayment"] },

                        // TODO: Why CHECKOUT_STARTED after a payment is started? (ask Andre)
                        CHECKOUT_STARTED: {
                            target: "#ecommerce.flow.checkout.validated",
                            actions: ["setCheckoutResponse"],
                        },

                        CHECKOUT_COMPLETED: { target: "completed", actions: ["setCheckoutResponse"] },
                        CHECKOUT_FAILED: { actions: ["setCheckoutResponse", "raiseFailedNotification"] },
                    },
                },
                paying: {
                    on: {
                        PAYMENT_CANCELLED: {
                            target: "#ecommerce.flow.checkout.validated",
                            actions: ["trackingEventPaymentCancelled", "resetPayment"],
                        },
                        PAYMENT_ERROR: {
                            target: "error",
                            actions: ["trackingEventPaymentError", "setPaymentError", "resetPayment"],
                        },
                        PAYMENT_CONFIRMED: { target: "completed", actions: ["setPaymentResult"] },
                    },
                },
                error: {
                    on: { START_PAYMENT: { target: "starting", actions: ["setPaymentTrackingId"] } },
                },
                completed: {
                    entry: ["trackingEventPaymentConfirmed"],
                    type: "final",
                },
            },
        },
        error: {},
    },
    on: {
        ERROR: { target: "#ecommerce.flow.error", actions: ["sendError"] },
        NOTIFY: { actions: ["sendNotify"] },
        SET_ACTIVE_STEP: { actions: ["setActiveStep"] },
        ALL_FARES_IN_ERROR: { actions: "checkAllFaresInError" },
        SET_PREFERRED_SUITE_NUMBER: { actions: ["setPreferredSuiteNumber"] },
        RESTORE_COMPLETED: { actions: ["setRestoreCompleted"] },
    },
})

type Context = ECommerceContext
type Events = ECommerceEvent

export const flowOptions = {
    guards: {
        isCourtesyHoldPage: (ctx: Context, ev: Events) => {
            assertEvent(ev, "CHECKOUT_STARTED")
            return (
                ev.response.flowType === "CourtesyHold" &&
                // NOTE: without the isRoute check you wouldn't be able to enter the payment step for any courtesy hold checkout
                ctx.flowVersion.router.isRoute(ctx.router, "thank-you-courtesy-hold")
            )
        },
    },
    actions: {
        ...trackingEventActions,
        ...submitStatusActions,
        ...fareCardActions,
        ...webQuoteActions,
        ...checkoutActions,
        ...paymentActions,
        ...vsCodeActions,

        restoreCheckout: actions.pure<Context, Events>((ctx) => {
            // TODO: sessionId should be a sum type of "from-query" | "new" to understand when to restore or not. It's passed when context is initialized in useCreateServices
            const checkout = getFromNormalQuery(ctx.router, "checkout")
            if (!checkout) return [raise({ type: "NO_CHECKOUT_TO_RESTORE" })]
            return F.pipe(
                CheckoutProcessId.strictType.decode(checkout),
                E.fold(
                    () => [notifyInvalidParameter("checkout", checkout)],
                    (checkoutId) => [raise<Context, Events, Events>({ type: "LOAD_EXISTING_CHECKOUT", checkoutId })],
                ),
            )
        }),
        restoreContextFromCheckout: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "CHECKOUT_STARTED", "CHECKOUT_COMPLETED")
            const checkout = ev.response
            const validatedPrice = checkout.validatedPrice

            if (validatedPrice.cruiseCode !== ctx.cruiseCode)
                return [
                    notifyInvalidParameter("checkout", {
                        checkoutCruiseCode: validatedPrice.cruiseCode,
                        pageCruiseCode: ctx.cruiseCode,
                    }),
                ]

            const vsOption = validatedPrice.vsMember ? O.some(VsOption.build({ type: "VS", percentage: 0 })) : O.none
            const fareCard: FareCard = {
                key: fareCardKey(validatedPrice.fareCode, validatedPrice.air.type === "flight"),
                index: 0 as FareCard["index"],
                fare: validatedPrice.fareCode,
                air: validatedPrice.air,
                options: SingleFareOptions.build({
                    fareCode: validatedPrice.fareCode,
                    vs: vsOption,
                    airFares: [],
                    gateways: F.pipe(
                        AirParams.maybeGateway(validatedPrice.air),
                        O.map((code) => ({ code, name: code })),
                        A.fromOption,
                    ),
                    d2d: O.none,
                    promotions: [],
                }),
                vsEligibility: vsEligibility(vsOption),
                status: "good",
                feasibleAirFares: FeasibleAirFares.noIncluded(),
                response: validatedPrice,
                fareLabel: fareCodeLabel(ctx.sanityContent.faresContent, validatedPrice.fareCode),
                suitePrices: [E.right(validatedPrice)],
            }
            return ctx.flowVersion.stateMachine.flow.raiseEventsForCheckoutRestored(checkout, fareCard)
        }),

        raiseCheckoutRestored: raise<Context, Events>({ type: "CHECKOUT_RESTORED" }),

        setGuests: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "CHANGE_GUESTS")
            return raiseGuestsChanged(ev.guests)
        }),
        setPaymentTerms: assign<Context, Events>({
            paymentTerms: (ctx, ev) => {
                assertEvent(ev, "CRUISE_PAYMENT_TERMS_LOADED")
                return Object.fromEntries(ev.response.map((x) => [x.fareCode, x]))
            },
        }),

        setSelectedSuiteCategory: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "SELECT_SUITE_CATEGORY")
            if (!ctx.suiteCategoryValidator.isValid(ev.suiteCategory))
                return [notifyInvalidParameter("selectedSuiteCategory", ev.suiteCategory)]
            if (ctx.selectedSuiteCategory === ev.suiteCategory) return []
            return raiseStandardSuiteCategorySelected(ev.suiteCategory, ev.isAutomaticSelection)
        }),
        setSelectedQuoteSuiteCategory: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "SELECT_QUOTE_SUITE_CATEGORY")
            if (!ctx.suiteCategoryValidator.isValid(ev.suiteCategory))
                return [notifyInvalidParameter("selectedSuiteCategory", ev.suiteCategory)]
            if (ctx.selectedSuiteCategory === ev.suiteCategory) return []
            return [
                assign<Context, Events>({
                    selectedSuiteCategory: ev.suiteCategory,
                    selectedSuiteCategorySource: "quote",
                }),
                raise<Context, Events, Events>({
                    type: "QUOTE_SUITE_CATEGORY_SELECTED",
                    suiteCategory: ev.suiteCategory,
                }),
            ]
        }),
        clearSelectedSuiteCategory: actions.pure<Context, Events>((ctx) => {
            if (ctx.selectedSuiteCategory)
                return [
                    assign<Context, Events>({
                        selectedSuiteCategory: undefined,
                        selectedSuiteCategorySource: undefined,
                        isAutomaticSelection: false,
                    }),
                    raise<Context, Events, Events>({
                        type: "SUITE_CATEGORY_UNSELECTED",
                    }),
                ]

            return undefined
        }),

        setSelectedSuiteNumber: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "SELECT_SUITE_NUMBER")
            if (!ctx.suiteNumberValidator.isValid(ev.suiteNumber))
                return [notifyInvalidParameter("selectedSuiteNumber", ev.suiteNumber)]
            if (ctx.selectedSuiteNumber === ev.suiteNumber) return []
            return raiseSuiteNumberSelected(ev.suiteNumber)
        }),
        setPreferredSuiteNumber: assign<Context, Events>({
            preferredSuiteNumber: (ctx, ev) => {
                assertEvent(ev, "SET_PREFERRED_SUITE_NUMBER")
                return ev.suiteNumber
            },
        }),
        clearSelectedSuiteNumber: actions.pure<Context, Events>((ctx) => {
            if (!ctx.selectedSuiteNumber) return undefined

            return [
                assign<Context, Events>({ selectedSuiteNumber: () => undefined }),
                raise<Context, Events, Events>({ type: "SUITE_NUMBER_UNSELECTED" }),
            ]
        }),

        setActiveStep: actions.assign<Context, Events>({
            activeStep: (ctx, ev) => {
                assertEvent(ev, "SET_ACTIVE_STEP")
                return ev.activeStep
            },
        }),
        setSessionId: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "LOAD_EXISTING_CHECKOUT")
            return [
                assign<Context, Events>({ sessionId: ev.checkoutId }),
                raise<Context, Events, Events>({ type: "SESSION_ID_CHANGED", sessionId: ev.checkoutId }),
            ]
        }),
        setNoFlight: actions.pure<Context, Events>((ctx) => {
            if (featureFlag("flights-step", ctx.router)) return undefined

            return raiseFlightSelected(noFlightKey)
        }),
        setSelectedFlight: actions.pure<Context, Events>((ctx, ev) => {
            assertEvent(ev, "SELECT_FLIGHT")
            return raiseFlightSelected(ev.flightNumber)
        }),
        clearSelectedFlight: actions.pure<Context, Events>((ctx) => {
            if (!ctx.selectedFlightNumber) return undefined

            return [
                assign<Context, Events>({ selectedFlightNumber: undefined }),
                raise<Context, Events, Events>({ type: "FLIGHT_UNSELECTED" }),
            ]
        }),
        setRestoreCompleted: actions.assign<Context, Events>({ isRestoreCompleted: true }),
    },
}
